import { useState } from 'react'
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { Button, Card } from '@/components/common'
import { TransactionCard } from '@/components/finance'
import { useGetTransactionsQuery, useGetFinancialSummaryQuery } from '@/store'

export default function TransactionListPage() {
  const [selectedType, setSelectedType] = useState<'all' | 'income' | 'expense'>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Fetch transactions
  const { data: transactionsData, isLoading } = useGetTransactionsQuery({
    type: selectedType === 'all' ? undefined : selectedType,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    page: 1,
    limit: 20,
  })

  // Fetch summary
  const { data: summary } = useGetFinancialSummaryQuery({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  })

  const transactions = transactionsData?.data || []

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-text-primary">การเงิน</h1>
          <p className="text-text-secondary mt-1">
            รายรับ-รายจ่ายทั้งหมด
          </p>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Total Income */}
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-tertiary mb-1">รายรับทั้งหมด</p>
                  <p className="text-2xl font-bold text-success">
                    {summary.totalIncome.toLocaleString('th-TH')} ฿
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-success-light flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-success" />
                </div>
              </div>
            </Card>

            {/* Total Expense */}
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-tertiary mb-1">รายจ่ายทั้งหมด</p>
                  <p className="text-2xl font-bold text-error">
                    {summary.totalExpense.toLocaleString('th-TH')} ฿
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-error-light flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-error" />
                </div>
              </div>
            </Card>

            {/* Net Profit */}
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-tertiary mb-1">กำไร/ขาดทุน</p>
                  <p
                    className={`text-2xl font-bold ${
                      summary.netProfit >= 0 ? 'text-success' : 'text-error'
                    }`}
                  >
                    {summary.netProfit.toLocaleString('th-TH')} ฿
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-info-light flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-info" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-border-light">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Type filter */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                ประเภท
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as any)}
                className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
              >
                <option value="all">ทั้งหมด</option>
                <option value="income">รายรับ</option>
                <option value="expense">รายจ่าย</option>
              </select>
            </div>

            {/* Start date */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                วันที่เริ่มต้น
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
              />
            </div>

            {/* End date */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                วันที่สิ้นสุด
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
              />
            </div>

            {/* Clear button */}
            <div className="flex items-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setSelectedType('all')
                  setStartDate('')
                  setEndDate('')
                }}
                className="w-full"
              >
                ล้างตัวกรอง
              </Button>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800"></div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="bg-background-secondary rounded-lg p-12 text-center">
            <p className="text-text-tertiary text-lg">ไม่พบรายการ</p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {transactionsData && transactionsData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="secondary" disabled>
              ก่อนหน้า
            </Button>
            <span className="px-4 py-2 text-text-secondary">
              หน้า 1 / {transactionsData.pagination.totalPages}
            </span>
            <Button variant="secondary" disabled>
              ถัดไป
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
